import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Clock, Mail, ChevronDown, ChevronUp } from "lucide-react";
import { useAppState } from "../hooks/useAppState";
import { useAuth } from "../hooks/useAuth";
import TopBar from "../components/layout/TopBar";
import ClientModal from "../components/clients/ClientModal";
import { getClientColor, getClientInitials } from "../utils/clientColors";
import { COMPENSATION_TYPES } from "../constants/timesheetStatus";
import { subscribeToEmailLog } from "../firebase/firestoreService";

const COMP_LABEL_MAP = {
  fixed: "Fisso",
  hourly: "Oraria",
  daily: "Giornaliera",
  project: "Progetto",
};

export default function ClientsPage() {
  const {
    user,
    darkMode,
    toggleDarkMode,
    isSynced,
    clients,
    invoices,
    createClient,
    updateClient,
    deleteClient,
  } = useAppState();

  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [expandedClient, setExpandedClient] = useState(null);
  const [emailLog, setEmailLog] = useState([]);

  // Subscribe to email log
  useEffect(() => {
    if (!authUser?.uid) return;
    const unsub = subscribeToEmailLog(authUser.uid, setEmailLog);
    return () => unsub();
  }, [authUser?.uid]);

  // Stats fatturato per cliente
  const clientStats = useMemo(() => {
    const stats = {};
    for (const inv of invoices) {
      const name = (inv.clientName || "").trim().toLowerCase();
      if (!name) continue;
      if (!stats[name]) stats[name] = { total: 0, count: 0 };
      stats[name].total += Number(inv.grossAmount) || 0;
      stats[name].count += 1;
    }
    return stats;
  }, [invoices]);

  // Filtro clienti
  const filteredClients = useMemo(() => {
    let list = [...clients];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.contactPerson || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q)
      );
    }
    if (filterType) {
      list = list.filter((c) => c.compensationType === filterType);
    }
    return list;
  }, [clients, searchQuery, filterType]);

  const handleSave = async (formData) => {
    if (editingClient) {
      await updateClient(editingClient.id, formData);
    } else {
      await createClient(formData);
    }
    setModalOpen(false);
    setEditingClient(null);
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setModalOpen(true);
  };

  const handleDelete = async (client) => {
    if (window.confirm(`Eliminare il cliente "${client.name}"?`)) {
      await deleteClient(client.id);
    }
  };

  const handleNewTimesheet = (client) => {
    navigate(`/timesheet?clientId=${client.id}`);
  };

  const formatAmount = (amount) =>
    new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(amount);

  return (
    <div className={`min-h-screen ${darkMode ? "bg-neutral-950 text-white" : "bg-neutral-50 text-neutral-900"}`}>
      <TopBar user={user} darkMode={darkMode} toggleDarkMode={toggleDarkMode} isSynced={isSynced} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-xl font-bold">Clienti</h1>
          <button
            onClick={() => {
              setEditingClient(null);
              setModalOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-forfy-500 text-white rounded-lg text-sm font-medium hover:bg-forfy-600 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nuovo cliente
          </button>
        </div>

        {/* Filtri */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${darkMode ? "text-neutral-500" : "text-neutral-400"}`} />
            <input
              type="text"
              placeholder="Cerca cliente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-3 py-2 border rounded-lg text-sm ${
                darkMode
                  ? "border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500"
                  : "border-neutral-200 bg-white text-neutral-900 placeholder-neutral-400"
              }`}
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className={`px-3 py-2 border rounded-lg text-sm ${
              darkMode
                ? "border-neutral-700 bg-neutral-800 text-white"
                : "border-neutral-200 bg-white text-neutral-900"
            }`}
          >
            <option value="">Tutti i tipi</option>
            {COMPENSATION_TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Griglia clienti */}
        {filteredClients.length === 0 ? (
          <div className={`text-center py-16 rounded-xl border ${darkMode ? "border-neutral-800 bg-neutral-900" : "border-neutral-200 bg-white"}`}>
            <p className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
              {clients.length === 0
                ? "Nessun cliente ancora. Crea il primo!"
                : "Nessun risultato per la ricerca."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClients.map((client) => {
              const color = getClientColor(client.name);
              const initials = getClientInitials(client.name);
              const stats = clientStats[(client.name || "").trim().toLowerCase()] || { total: 0, count: 0 };

              return (
                <div
                  key={client.id}
                  className={`rounded-xl border p-4 transition-colors ${
                    darkMode
                      ? "border-neutral-800 bg-neutral-900 hover:border-neutral-700"
                      : "border-neutral-200 bg-white hover:border-neutral-300"
                  }`}
                >
                  {/* Top row: avatar + info */}
                  <div className="flex items-start gap-3">
                    <div
                      className={`h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0 ${color.bg}`}
                    >
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className={`text-sm font-semibold truncate ${darkMode ? "text-white" : "text-neutral-900"}`}>
                        {client.name}
                      </h3>
                      {client.contactPerson && (
                        <p className={`text-xs truncate ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                          {client.contactPerson}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                            darkMode ? "bg-neutral-800 text-neutral-400" : "bg-neutral-100 text-neutral-600"
                          }`}
                        >
                          {COMP_LABEL_MAP[client.compensationType] || client.compensationType}
                        </span>
                        {client.rate > 0 && client.compensationType !== "project" && (
                          <span className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
                            {client.rate.toLocaleString("it-IT")} €
                            {client.compensationType === "daily" ? "/gg" : client.compensationType === "hourly" ? "/h" : "/mese"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className={`mt-3 pt-3 border-t flex items-center justify-between ${darkMode ? "border-neutral-800" : "border-neutral-100"}`}>
                    <div>
                      <p className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>Fatturato</p>
                      <p className={`text-sm font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
                        {formatAmount(stats.total)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>Fatture</p>
                      <p className={`text-sm font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
                        {stats.count}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className={`mt-3 pt-3 border-t flex items-center gap-2 ${darkMode ? "border-neutral-800" : "border-neutral-100"}`}>
                    <button
                      onClick={() => handleEdit(client)}
                      className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        darkMode
                          ? "text-neutral-400 hover:bg-neutral-800 hover:text-white"
                          : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                      }`}
                    >
                      <Pencil className="h-3 w-3" />
                      Modifica
                    </button>
                    <button
                      onClick={() => handleNewTimesheet(client)}
                      className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        darkMode
                          ? "text-neutral-400 hover:bg-neutral-800 hover:text-white"
                          : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                      }`}
                    >
                      <Clock className="h-3 w-3" />
                      Timesheet
                    </button>
                    <button
                      onClick={() => setExpandedClient(expandedClient === client.id ? null : client.id)}
                      className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        darkMode
                          ? "text-neutral-400 hover:bg-neutral-800 hover:text-white"
                          : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                      }`}
                    >
                      <Mail className="h-3 w-3" />
                      Email
                      {expandedClient === client.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                    <button
                      onClick={() => handleDelete(client)}
                      className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-auto"
                    >
                      <Trash2 className="h-3 w-3" />
                      Elimina
                    </button>
                  </div>

                  {/* Email inviate */}
                  {expandedClient === client.id && (() => {
                    const clientEmails = emailLog.filter((e) => e.clientId === client.id);
                    return (
                      <div className={`mt-3 pt-3 border-t ${darkMode ? "border-neutral-800" : "border-neutral-100"}`}>
                        <p className={`text-xs font-medium mb-2 ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}>
                          Email inviate ({clientEmails.length})
                        </p>
                        {clientEmails.length === 0 ? (
                          <p className={`text-xs ${darkMode ? "text-neutral-600" : "text-neutral-400"}`}>
                            Nessuna email inviata a questo cliente.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {clientEmails.slice(0, 5).map((email) => (
                              <div
                                key={email.id}
                                className={`p-2 rounded-lg text-xs ${
                                  darkMode ? "bg-neutral-800" : "bg-neutral-50"
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className={`font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                                    {email.type === "sollecito" ? "Sollecito" : email.type === "timesheet" ? "Timesheet" : email.type}
                                  </span>
                                  <span className={`${
                                    email.status === "sent"
                                      ? darkMode ? "text-green-400" : "text-green-600"
                                      : darkMode ? "text-red-400" : "text-red-600"
                                  }`}>
                                    {email.status === "sent" ? "Inviata" : "Fallita"}
                                  </span>
                                </div>
                                <p className={`mt-0.5 ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                                  {email.sentTo} — {email.createdAt?.toDate ? email.createdAt.toDate().toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </main>

      <ClientModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingClient(null);
        }}
        onSave={handleSave}
        client={editingClient}
        darkMode={darkMode}
      />
    </div>
  );
}
